import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Boxes, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { useFeature } from '@/hooks/useFeature';
import { useRole } from '@/hooks/useRole';
import { supabase } from '@/integrations/supabase/client';
import { useStock, useRegisterStockMovement } from '@/hooks/useStock';
import { toDeductionLines, type DeductionLine, type DeductionSourceItem } from '@/lib/stock';
import { variantDisplayName } from '@/lib/variants';
import { useStockSettings } from '@/hooks/useStockSettings';
import { plural } from '@/lib/plural';
import { resolveProductStockAction } from '@/lib/stockSettings';

interface Props {
  /** Itens do orçamento/OS (com product_id + quantity). */
  items: DeductionSourceItem[];
  /** Exatamente um destes: origem da baixa. */
  quoteId?: string | null;
  serviceOrderId?: string | null;
  /**
   * Só para OS: o orçamento de onde ela nasceu (`service_orders.quote_id`).
   * É o que fecha o guard de CADEIA — sem isto a OS não tem como saber que a
   * venda já baixou estoque do outro lado. Ver `chainProductIds`.
   */
  originQuoteId?: string | null;
}

const dismissKey = (kind: string, id: string) => `orcamigo_stock_deducted_${kind}_${id}`;

/**
 * Convida a dar baixa no estoque quando um orçamento é CONCLUÍDO ou uma OS é
 * concluída — o passo que representa a entrega, nunca a aprovação (ver
 * `stockSettings.ts`). Lista SÓ os itens-produto RASTREADOS (serviços e produtos
 * sem rastreio são ignorados).
 *
 * ─── GUARD DE CADEIA (uma baixa por VENDA, não por documento) ───────────────
 *
 * O guard antigo perguntava só "este documento já baixou?" (`quote_id` OU
 * `service_order_id`). Isso deixava passar a duplicação mais comum do app:
 * orçamento concluído baixa 2, a OS gerada DELE é concluída e baixa mais 2 —
 * 4 unidades para um serviço que consome 2. Medido em device real.
 *
 * Agora a pergunta é "a CADEIA já baixou?", nos dois sentidos, porque qualquer
 * um dos lados pode ser concluído primeiro:
 *   - numa OS: olha também o `quote_sale` do orçamento de origem;
 *   - num orçamento: olha também o `service_order_sale` das OSs nascidas dele.
 *
 * É o mesmo raciocínio que o DRE já aplica ao custo (`profitReport` pula OS com
 * `quote_id`): orçamento e a OS derivada são o mesmo negócio, contado uma vez.
 * OS avulsa (sem `quote_id`) não tem cadeia e segue com o guard do documento.
 */
export function StockDeductionPrompt({ items, quoteId, serviceOrderId, originQuoteId }: Props) {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const inventory = useFeature('inventory');
  const { canWrite } = useRole();
  const { trackedIds, trackedProducts } = useStock();
  const register = useRegisterStockMovement();
  const queryClient = useQueryClient();
  const { settings } = useStockSettings();
  /** Garante uma única execução do modo automático por montagem. */
  const autoRan = useRef(false);

  const kind = quoteId ? 'quote' : 'so';
  const sourceId = (quoteId ?? serviceOrderId ?? '') as string;
  const reason = quoteId ? ('quote_sale' as const) : ('service_order_sale' as const);
  const isRealSource = Boolean(sourceId) && !String(sourceId).startsWith('offline_');

  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(dismissKey(kind, sourceId)) === '1';
    } catch {
      return false;
    }
  });
  const [open, setOpen] = useState(false);
  const [qtyById, setQtyById] = useState<Record<string, string>>({});

  // Linhas de baixa: só itens cujo product_id está rastreado.
  // O rótulo precisa distinguir VARIAÇÕES: a filha guarda o nome do pai em
  // `name` (o que a distingue é `variant_name`), então duas variações do mesmo
  // produto sairiam como duas linhas de texto idêntico — o usuário não saberia
  // qual é qual nem o que está confirmando.
  const nameById = useMemo(
    () =>
      new Map(
        trackedProducts.map((p: any) => [String(p.id), variantDisplayName(p.name, p.variant_name)]),
      ),
    [trackedProducts],
  );
  const lines = useMemo(
    () => toDeductionLines(items ?? [], trackedIds, nameById),
    [items, trackedIds, nameById],
  );

  const modeById = useMemo(
    () => new Map(
      trackedProducts.map((product: any) => [
        String(product.id),
        product.stock_deduction_mode ?? null,
      ]),
    ),
    [trackedProducts],
  );
  const triggerKey = quoteId ? 'quote_completed' : 'service_order_completed';
  /**
   * O orçamento que amarra a cadeia: o próprio (quando estamos num orçamento) ou
   * o de origem (quando estamos numa OS gerada dele). `null` = OS avulsa.
   */
  const chainQuoteId = (quoteId ?? originQuoteId) || null;
  const isRealChain = Boolean(chainQuoteId) && !String(chainQuoteId).startsWith('offline_');

  // Guard servidor: já existe baixa para esta origem OU para a cadeia dela?
  const { data: deductedProductIds, isSuccess: guardResolved } = useQuery({
    queryKey: ['stock-deducted', kind, sourceId, isRealChain ? chainQuoteId : null, activeCompany?.id],
    enabled:
      Boolean(user?.id && activeCompany?.id) &&
      inventory.allowed &&
      isRealSource &&
      lines.length > 0,
    networkMode: 'offlineFirst',
    staleTime: 60 * 1000,
    queryFn: async () => {
      const companyId = activeCompany!.id;
      const deducted = new Set<string>();
      const add = (rows: { product_id: unknown }[] | null) => {
        for (const row of rows ?? []) deducted.add(String(row.product_id));
      };

      // 1) O próprio documento.
      let own = supabase
        .from('stock_movements')
        .select('product_id')
        .eq('company_id', companyId)
        .eq('reason', reason);
      own = quoteId ? own.eq('quote_id', quoteId) : own.eq('service_order_id', serviceOrderId!);
      const { data: ownRows, error: ownError } = await own;
      if (ownError) throw ownError;
      add(ownRows as any);

      // 2) A cadeia orçamento↔OS. Sem orçamento na jogada não há o que cruzar.
      if (isRealChain) {
        // 2a) Estamos na OS: o orçamento de origem já baixou?
        if (!quoteId) {
          const { data, error } = await supabase
            .from('stock_movements')
            .select('product_id')
            .eq('company_id', companyId)
            .eq('reason', 'quote_sale')
            .eq('quote_id', chainQuoteId!);
          if (error) throw error;
          add(data as any);
        }

        // 2b) Alguma OS nascida deste orçamento já baixou? Cobre o caminho
        //     inverso (a OS é concluída antes de o orçamento ser concluído),
        //     que sem isto baixaria de novo ao concluir o orçamento.
        const { data: orders, error: ordersError } = await supabase
          .from('service_orders')
          .select('id')
          .eq('company_id', companyId)
          .eq('quote_id', chainQuoteId!);
        if (ordersError) throw ordersError;
        const siblingIds = (orders ?? [])
          .map((row: any) => String(row.id))
          .filter((orderId) => orderId !== serviceOrderId);
        if (siblingIds.length > 0) {
          const { data, error } = await supabase
            .from('stock_movements')
            .select('product_id')
            .eq('company_id', companyId)
            .eq('reason', 'service_order_sale')
            .in('service_order_id', siblingIds);
          if (error) throw error;
          add(data as any);
        }
      }

      return deducted;
    },
  });

  const pendingLines = useMemo(
    () => lines.filter((line) => !deductedProductIds?.has(line.product_id)),
    [lines, deductedProductIds],
  );
  const autoLines = useMemo(
    () => pendingLines.filter((line) =>
      resolveProductStockAction(modeById.get(line.product_id), settings, triggerKey) === 'auto'),
    [pendingLines, modeById, settings, triggerKey],
  );
  const askLines = useMemo(
    () => pendingLines.filter((line) =>
      resolveProductStockAction(modeById.get(line.product_id), settings, triggerKey) === 'ask'),
    [pendingLines, modeById, settings, triggerKey],
  );

  const canAct =
    inventory.allowed && canWrite && isRealSource && pendingLines.length > 0;

  const handleDismiss = () => {
    try {
      localStorage.setItem(dismissKey(kind, sourceId), '1');
    } catch {
      // ignore
    }
    setDismissed(true);
  };

  const openModal = () => {
    // Semeia as quantidades com as do documento.
    const seed: Record<string, string> = {};
    for (const l of askLines) seed[l.product_id] = String(l.quantity).replace('.', ',');
    setQtyById(seed);
    setOpen(true);
  };

  /**
   * Executa a baixa. Compartilhada pelo modo manual (o usuário confirmou as
   * quantidades) e pelo automático — uma segunda cópia divergiria justamente
   * nos guards anti-dupla-baixa, que é o que não pode falhar.
   */
  const runDeduction = async (
    toDeduct: { line: DeductionLine; qty: number }[],
    opts?: { auto?: boolean },
  ) => {
    const valid = toDeduct.filter((x) => Number.isFinite(x.qty) && x.qty > 0);
    if (valid.length === 0) {
      if (!opts?.auto) toast.warning('Informe ao menos uma quantidade para dar baixa.');
      return;
    }

    try {
      for (const { line, qty } of valid) {
        await register.mutateAsync({
          productId: line.product_id,
          delta: -qty,
          reason,
          quoteId: quoteId ?? null,
          serviceOrderId: serviceOrderId ?? null,
        });
      }
      // No manual, o marcador local fecha o convite. No automático ele NÃO pode
      // esconder linhas `ask` do mesmo documento; o guard por product_id basta.
      if (!opts?.auto) {
        try {
          localStorage.setItem(dismissKey(kind, sourceId), '1');
        } catch {
          // ignore
        }
      }
      queryClient.invalidateQueries({ queryKey: ['stock-deducted', kind, sourceId, activeCompany?.id] });
      // O toast do modo automático DIZ que foi automático: estoque que se mexe
      // sozinho e em silêncio é indistinguível de bug.
      toast.success(
        opts?.auto
          ? `Baixa automática de estoque registrada (${plural(valid.length, 'produto', 'produtos')}).`
          : 'Baixa de estoque registrada.',
      );
      setOpen(false);
      if (!opts?.auto) setDismissed(true);
    } catch {
      // erro individual já exibido pela mutation
    }
  };

  const handleConfirm = () =>
    runDeduction(
      askLines.map((l) => ({
        line: l,
        qty: Number(String(qtyById[l.product_id] ?? '').replace(',', '.')),
      })),
    );

  /**
   * Modo automático: dá a baixa sozinho assim que a tela reconhece a origem.
   *
   * Os MESMOS guards do modo manual valem (`alreadyDeducted` no servidor + o
   * marcador em localStorage), senão reabrir a tela baixaria de novo. O `ref`
   * cobre o intervalo em que a query de guard ainda revalida — sem ele, dois
   * renders seguidos disparariam duas baixas.
   *
   * ⚠️ O hook fica ANTES do `return null` e DEPOIS de `runDeduction`: antes do
   * return porque a ordem dos hooks não pode variar entre renders; depois da
   * declaração porque `const` em TDZ estoura se o efeito rodar num render que
   * saiu antes de declará-la — e o modo 'auto' é justamente o que sai antes.
   */
  useEffect(() => {
    // ⚠️ `guardResolved`: enquanto a consulta de dupla-baixa não respondeu,
    // `alreadyDeducted` é `undefined` — e `!undefined` é `true`, então `canAct`
    // já estaria liberado. No modo manual isso não importa (o clique demora
    // segundos e a consulta chega antes), mas o automático dispara no primeiro
    // render: sem esta espera, abrir num OUTRO dispositivo um orçamento já
    // baixado (onde não existe o marcador local) baixaria o estoque DE NOVO.
    // Se a consulta não resolver (offline), o automático simplesmente não
    // roda — errar para o lado de não mexer no estoque é o certo.
    if (
      !guardResolved
      || !canAct
      || autoLines.length === 0
      || autoRan.current
      || register.isPending
    ) return;
    autoRan.current = true;
    void runDeduction(autoLines.map((l) => ({ line: l, qty: l.quantity })), { auto: true });
    // runDeduction/lines são recriados a cada render; o guard `autoRan` é o que
    // garante execução única, não a lista de deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLines, guardResolved, canAct, register.isPending]);

  // Baixa é uma escrita: só membros com permissão de escrita (a RPC também
  // rejeita no servidor, mas escondemos a ação para não mostrar erro a viewers).
  // 'off' e 'auto' não mostram o convite: 'off' porque o usuário desligou o
  // gatilho, 'auto' porque não há o que perguntar.
  if (!canAct || dismissed || askLines.length === 0) {
    return null;
  }

  return (
    <>
      <Card className="border-amber-200 bg-amber-50/60 dark:border-amber-900/50 dark:bg-amber-950/20">
        <CardContent className="flex items-center gap-3 p-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-950/40">
            <Boxes className="h-5 w-5 text-amber-600" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium">Dar baixa no estoque?</p>
            <p className="text-xs text-muted-foreground">
              {askLines.length === 1 ? '1 produto rastreado' : `${askLines.length} produtos rastreados`} nesta venda.
            </p>
          </div>
          <Button3D size="sm" onClick={openModal}>
            Dar baixa
          </Button3D>
          <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={handleDismiss}>
            <X className="h-4 w-4" />
          </Button>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Dar baixa no estoque</DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Confirme as quantidades a subtrair do estoque. Serviços e produtos sem rastreio não entram.
            </p>
            {askLines.map((l) => (
              <div key={l.product_id} className="flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{l.name}</p>
                </div>
                <div className="w-28 shrink-0">
                  <Label htmlFor={`qty-${l.product_id}`} className="sr-only">
                    Quantidade de {l.name}
                  </Label>
                  <Input
                    id={`qty-${l.product_id}`}
                    inputMode="decimal"
                    value={qtyById[l.product_id] ?? ''}
                    onChange={(e) => setQtyById((prev) => ({ ...prev, [l.product_id]: e.target.value }))}
                  />
                </div>
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button3D color="gray" onClick={() => setOpen(false)}>
              Cancelar
            </Button3D>
            <Button3D onClick={handleConfirm} disabled={register.isPending}>
              {register.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Confirmar baixa
            </Button3D>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
